import { Box, Caption, Divider, Heading, Info, Success, TextBlock } from "@bcc-ui/ui-components";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Failure } from "components/icons/Failure";
import style from "components/reports/passports/passports-view.scss";

const STATUS_ICON_SIZE_PIXELS = 120;

export const isPass = (value: string | undefined): boolean | undefined => {
    const diagnosticValue = value?.toLowerCase();
    if (diagnosticValue === "successful") {
        return true;
    }
    return diagnosticValue === "failed" ? false : undefined;
};

interface PanelStatus {
    icon: React.ReactNode;
    label: string;
    labelClassName: string;
}

export const getDiagnosticStatus = (
    passed: boolean | undefined,
    t: (key: string) => string
): PanelStatus | undefined => {
    if (passed === undefined) {
        return undefined;
    }
    return {
        icon: passed ? <Success size={STATUS_ICON_SIZE_PIXELS} /> : <Failure size={STATUS_ICON_SIZE_PIXELS} />,
        label: t(passed ? "Common.state.passed" : "Common.state.failed"),
        labelClassName: passed ? style.resultLabelPassed : style.resultLabelFailed,
    };
};

const renderStatus = (status: PanelStatus) => (
    <Box
        columnLayout
        horizontalAlignment="Center"
        verticalAlignment="Center"
        gap="Xxs"
        className={style.diagnosticResult}
    >
        {status.icon}
        <span className={status.labelClassName}>{status.label}</span>
    </Box>
);

interface PanelProps {
    title: string;
    description: string;
    icon: React.ReactNode;
    rows?: { label?: string; value: React.ReactNode }[];
    status?: PanelStatus;
}

export const Panel = (props: PanelProps) => {
    const { t } = useTranslation();
    const notAvailable: PanelStatus = {
        icon: <Info size={STATUS_ICON_SIZE_PIXELS} />,
        label: t("Common.dataNotAvailable"),
        labelClassName: style.notAvailableLabel,
    };
    return (
        <Box padding="Md" columnLayout className={style.detailPanel}>
            <div className={style.detailPanelHeader}>
                <span className={style.detailPanelIcon}>{props.icon}</span>
                <Heading tag="div" variant="SUBTITLE_1" disableTopSpacing disableBottomSpacing>
                    {props.title}
                </Heading>
                <Caption greyTextStyle className={style.detailPanelDescription}>
                    {props.description}
                </Caption>
            </div>
            <Divider direction="Horizontal" />
            {props.rows ? (
                <Box columnLayout className={style.detailPanelRows}>
                    {props.rows.map((row) => (
                        <Box
                            key={row.label}
                            justifyContent="SpaceBetween"
                            verticalAlignment="Center"
                            gap="Md"
                            paddingTop="Xxs"
                            paddingBottom="Xxs"
                            className={style.detailRow}
                        >
                            <TextBlock disableBottomSpacing>{row.label}</TextBlock>
                            <TextBlock disableBottomSpacing className={style.detailValue}>
                                {row.value}
                            </TextBlock>
                        </Box>
                    ))}
                </Box>
            ) : (
                renderStatus(props.status ?? notAvailable)
            )}
        </Box>
    );
};
